if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    quantityInput = undefined;
    quantityForm = undefined;
    onVariantChangeUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;
    abortController = undefined;
    pendingRequestUrl = null;
    variantRequest = null;
    failedVariantRequest = null;
    otfSelectionRevision = 0;

    constructor() {
      super();
      this.quantityInput = this.querySelector('.quantity__input');
    }

    connectedCallback() {
      this.initializeProductSwapUtility();

      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.initQuantityHandlers();
      this.updateOtfSelection(false);
      queueMicrotask(() => this.initializeOtfFeaturedColor());
    }

    async initializeOtfFeaturedColor() {
      if (!this.isConnected || this.dataset.updateUrl !== 'true' || this.dataset.otfNeedsSize !== 'true') return;
      const params = new URLSearchParams(window.location.search);
      const colorId = params.get('otf_color');
      if (!colorId || !/^\d+$/.test(colorId) || params.has('variant') || params.has('option_values')) return;
      if (window.location.pathname !== this.dataset.url) return;
      const picker = this.querySelector('variant-radios, variant-selects');
      if (!picker) return;
      const revision = this.otfSelectionRevision;
      const pageUrl = window.location.href;
      const productUrl = this.dataset.url;
      const selectionSnapshot = () => JSON.stringify(
        Array.from(picker.querySelectorAll('input:checked, option:checked'), (option) => option.dataset.optionValueId || option.value)
      );
      const selectedOptions = selectionSnapshot();
      await customElements.whenDefined(picker.localName);
      if (document.querySelector(`#MediaGallery-${this.dataset.section}`)) await customElements.whenDefined('media-gallery');
      if (
        !this.isConnected || this.dataset.otfNeedsSize !== 'true' ||
        this.otfSelectionRevision !== revision || !picker.isConnected ||
        this.querySelector('variant-radios, variant-selects') !== picker ||
        this.dataset.url !== productUrl || window.location.href !== pageUrl ||
        selectionSnapshot() !== selectedOptions
      ) return;
      const option = Array.from(picker?.querySelectorAll('[data-option-value-id]') || []).find(
        (input) => input.dataset.optionValueId === colorId && !input.closest('[data-otf-size]')
      );
      if (!option) return;
      const input = option.tagName === 'OPTION' ? option.closest('select') : option;
      if (!input || input.disabled) return;
      this.otfFeaturedColor = colorId;
      if (option.tagName === 'OPTION') input.value = option.value;
      else input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    initQuantityHandlers() {
      if (!this.quantityInput) return;

      this.quantityForm = this.querySelector('.product-form__quantity');
      if (!this.quantityForm) return;

      this.quantityLimitButtonState = null;
      this.setQuantityBoundries();
      if (!this.dataset.originalSection) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }
    }

    disconnectedCallback() {
      this.onVariantChangeUnsubscriber?.();
      this.abortController?.abort();
      this.abortController = undefined;
      this.variantRequest = null;
      this.failedVariantRequest = null;
      this.quantityRulesController?.abort();
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    initializeProductSwapUtility() {
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      const isMainVariant = this.contains(event.target);
      const stickyVariantPicker = document.querySelector(`#variant-radios-${this.dataset.section}-sticky, #variant-selects-${this.dataset.section}-sticky`);
      const isStickyVariant = stickyVariantPicker && stickyVariantPicker.contains(event.target);

      if (!isMainVariant && !isStickyVariant) return;
      this.otfSelectionRevision += 1;

      this.querySelector('pickup-availability')?.update?.(null);

      this.resetProductFormState();
      this.syncVariantPickers(target);
      this.updateOtfSelection(true);
      if (this.dataset.otfNeedsSize === 'true') {
        this.updateVariantInputs(null);
        this.updateURL(this.dataset.url, null);
        const button = this.productForm?.querySelector('[name="add"]');
        if (button) {
          button.disabled = true;
          button.querySelector('span').textContent = this.dataset.otfChooseSize;
        }
      }

      let optionValuesToUse = selectedOptionValues;
      if (isStickyVariant && !isMainVariant) {
        const mainVariantPicker = this.querySelector('variant-radios, variant-selects');
        if (mainVariantPicker) {
          optionValuesToUse = Array.from(mainVariantPicker.querySelectorAll('select option[selected], fieldset input:checked')).map(
            ({ dataset }) => dataset.optionValueId
          );
        }
      }

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, optionValuesToUse.filter(Boolean), shouldFetchFullPage),
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
          : this.handleUpdateProductInfo(productUrl),
      });
    }

    updateOtfSelection(loading) {
      const root = this.closest('[id^="MainProduct-"]') || this;
      root.dataset.otfSelectionLoading = String(loading);
      if (this.dataset.otfSizeRequired !== 'true') return;
      const picker = this.querySelector('variant-radios, variant-selects');
      if (!picker) return;
      const sizeGroups = Array.from(picker.querySelectorAll('[data-otf-size]'));
      const missing = sizeGroups.some(group => group.tagName === 'SELECT' ? !group.value : !group.querySelector('input:checked'));
      this.dataset.otfNeedsSize = String(missing);
      root.dataset.otfSizePending = String(missing);
      root.querySelectorAll('[data-otf-selected-option]').forEach(label => {
        const group = picker.querySelector(`[data-otf-option="${label.dataset.otfSelectedOption}"]`);
        const value = group?.tagName === 'SELECT' ? group.value : group?.querySelector('input:checked')?.value;
        label.textContent = value || this.dataset.otfChooseSize;
      });
      if (missing) this.updateVariantInputs(null);
    }

    syncVariantPickers(changedInput) {
      const mainVariantPicker = document.querySelector(`#variant-radios-${this.dataset.section}, #variant-selects-${this.dataset.section}`);
      const stickyVariantPicker = document.querySelector(`#variant-radios-${this.dataset.section}-sticky, #variant-selects-${this.dataset.section}-sticky`);

      if (!mainVariantPicker || !stickyVariantPicker) return;

      const isMainChanged = mainVariantPicker.contains(changedInput);
      const sourcePicker = isMainChanged ? mainVariantPicker : stickyVariantPicker;
      const targetPicker = isMainChanged ? stickyVariantPicker : mainVariantPicker;

      const sourceInputs = sourcePicker.querySelectorAll('input[type="radio"]:checked, select');

      sourceInputs.forEach((sourceInput) => {
        let targetInput;

        if (sourceInput.tagName === 'SELECT') {
          const selectName = sourceInput.getAttribute('name');
          let targetSelectName = selectName;

          if (isMainChanged) {
            targetSelectName = 'sticky-' + selectName;
          } else {
            targetSelectName = selectName.replace('sticky-', '');
          }

          targetInput = Array.from(targetPicker.querySelectorAll('select')).find(input => input.name === targetSelectName);

          if (targetInput) {
            targetInput.value = sourceInput.value;
            Array.from(targetInput.options).forEach(opt => opt.removeAttribute('selected'));
            if (targetInput.selectedOptions[0]) {
              targetInput.selectedOptions[0].setAttribute('selected', 'selected');
            }
          }
        } else if (sourceInput.type === 'radio') {
          const radioName = sourceInput.getAttribute('name');
          const radioValue = sourceInput.value;
          let targetRadioName = radioName;

          if (isMainChanged) {
            targetRadioName = 'sticky-' + radioName;
          } else {
            targetRadioName = radioName.replace('sticky-', '');
          }

          targetInput = Array.from(targetPicker.querySelectorAll('input[type="radio"]')).find(input => input.name === targetRadioName && input.value === radioValue);

          if (targetInput) {
            targetInput.checked = true;

            const optionIndex = radioName.replace('sticky-', '').split('-').pop();
            const selectedValueDisplay = targetPicker.querySelector(`.selected__value[data-option-index="${optionIndex}"]`);
            if (selectedValueDisplay) {
              selectedValueDisplay.textContent = `(${radioValue})`;
            }
          }
        }
      });
    }

    resetProductFormState() {
      const productForm = this.productForm;
      if (productForm) {
        const addButton = productForm.querySelector('[name="add"]');
        if (addButton) {
          addButton.setAttribute('disabled', 'disabled');
          addButton.removeAttribute('aria-disabled');
        }
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) variantInput.disabled = true;
        productForm.error = false;
        if (productForm.handleErrorMessage) {
          productForm.handleErrorMessage();
        }
      }
    }

    handleSwapProduct(productUrl, updateFullPage) {
      return (html) => {
        const variant = this.getSelectedVariant(html.querySelector('product-info'));
        this.updateURL(productUrl, variant?.id);
        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          const mainContent = document.querySelector('main');
          if (mainContent) {
            mainContent.innerHTML = html.querySelector('main').innerHTML;
          }
        } else {
          const productSection = this.closest('.shopify-section') || this;
          const newProductInfo = html.querySelector('product-info');
          if (newProductInfo) {
            this.innerHTML = newProductInfo.innerHTML;
            this.dataset.url = productUrl;
            this.cartUpdateUnsubscriber?.();
            this.cartUpdateUnsubscriber = undefined;
            this.quantityInput = this.querySelector('.quantity__input');
            this.quantityForm = undefined;
            this.initQuantityHandlers();
          }

          const newMediaWrapper = html.querySelector('.product__media-wrapper');
          const currentMediaWrapper = productSection.querySelector('.product__media-wrapper');
          if (newMediaWrapper && currentMediaWrapper) {
            currentMediaWrapper.innerHTML = newMediaWrapper.innerHTML;
          }

          const newMediaGallery = html.querySelector('.product__media-gallery');
          const currentMediaGallery = productSection.querySelector('.product__media-gallery');
          if (newMediaGallery && currentMediaGallery) {
            currentMediaGallery.innerHTML = newMediaGallery.innerHTML;
          }

          this.updateVariantInputs(variant?.id);

          const updateSourceFromDestination = (id) => {
            const sourceId = `${id}-${this.sectionId}`;
            const source = html.getElementById(sourceId);
            const destination = document.getElementById(sourceId);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList = source.classList;
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Sku');

          const productForm = document.getElementById(`product-form-${this.dataset.section}`);
          if (productForm) {
            const sourceForm = html.getElementById(`product-form-${this.sectionId}`);
            if (sourceForm) {
              const addButton = productForm.querySelector('[name="add"]');
              const sourceButton = sourceForm.querySelector('[name="add"]');
              if (addButton && sourceButton) {
                if (sourceButton.hasAttribute('disabled')) {
                  addButton.setAttribute('disabled', 'disabled');
                } else {
                  addButton.removeAttribute('disabled');
                }
                const addButtonText = addButton.querySelector('span');
                const sourceButtonText = sourceButton.querySelector('span');
                if (addButtonText && sourceButtonText) {
                  addButtonText.textContent = sourceButtonText.textContent;
                }
              }
            }
          }
        }
      };
    }

    showVariantRecovery(request, restoreFocus) {
      if (request !== this.variantRequest || !this.isConnected) return;
      this.querySelector('pickup-availability')?.update?.(null);
      this.resetProductFormState();
      this.updateVariantInputs(null);
      this.updateOtfSelection(true);
      this.failedVariantRequest = request;

      const productForm = this.productForm;
      productForm?.handleErrorMessage(productForm.dataset.otfSelectionError || window.cartStrings.error);
      const errorWrapper = productForm?.querySelector('.product-form__error-message-wrapper');
      if (!errorWrapper || !productForm.dataset.otfSelectionRetry) return;

      let retryButton = errorWrapper.querySelector('[data-otf-variant-retry]');
      if (!retryButton) {
        retryButton = document.createElement('button');
        retryButton.type = 'button';
        retryButton.className = 'btn btn--secondary otf-selection-retry';
        retryButton.dataset.otfVariantRetry = '';
        retryButton.addEventListener('click', () => {
          const failedRequest = this.failedVariantRequest;
          if (!failedRequest || failedRequest !== this.variantRequest || !this.isConnected) return;
          this.renderProductInfo(failedRequest, true);
        });
        errorWrapper.append(retryButton);
      }
      retryButton.textContent = productForm.dataset.otfSelectionRetry;
      retryButton.disabled = false;
      retryButton.hidden = false;
      if (restoreFocus) retryButton.focus({ preventScroll: true });
    }

    renderProductInfo(request, isRetry = false) {
      const { requestUrl, callback } = request;
      if (this.abortController) {
        this.abortController.abort();
      }
      const controller = new AbortController();
      this.abortController = controller;
      this.variantRequest = request;
      this.failedVariantRequest = null;
      const productForm = this.productForm;
      const retryButton = productForm?.querySelector('[data-otf-variant-retry]');
      const restoreFocus = isRetry && document.activeElement === retryButton;
      if (retryButton) retryButton.hidden = !isRetry;
      if (isRetry) {
        this.querySelector('pickup-availability')?.update?.(null);
        this.resetProductFormState();
        this.updateVariantInputs(null);
        this.updateOtfSelection(true);
        productForm?.handleErrorMessage(productForm.dataset.otfSelectionError || window.cartStrings.error);
        if (retryButton) {
          retryButton.disabled = true;
          retryButton.textContent = productForm.dataset.otfSelectionRetrying || productForm.dataset.otfSelectionRetry;
        }
      } else {
        productForm?.handleErrorMessage();
      }

      let timedOut = false;
      const requestTimeout = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 12000);
      const clearRequestTimeout = () => clearTimeout(requestTimeout);
      controller.signal.addEventListener('abort', clearRequestTimeout, { once: true });

      fetch(requestUrl, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Product options could not be loaded.');
          return response.text();
        })
        .then((responseText) => {
          if (controller !== this.abortController || controller.signal.aborted || !this.isConnected) return;
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (!html.querySelector('product-info')) throw new Error('Product options response was incomplete.');
          callback(html);
          if (controller !== this.abortController || controller.signal.aborted || !this.isConnected) return;
          this.productForm?.handleErrorMessage();
          if (retryButton) retryButton.hidden = true;
          if (restoreFocus) {
            const addButton = this.productForm?.querySelector('[name="add"]');
            const focusTarget = addButton && !addButton.disabled ? addButton : this.querySelector('variant-radios input:checked, variant-selects select');
            focusTarget?.focus({ preventScroll: true });
          }
        })
        .catch((error) => {
          if ((!timedOut && error.name === 'AbortError') || controller !== this.abortController || !this.isConnected) return;
          this.pendingRequestUrl = null;
          this.showVariantRecovery(request, restoreFocus);
          console.error(error);
        })
        .finally(() => {
          clearRequestTimeout();
          controller.signal.removeEventListener('abort', clearRequestTimeout);
        });
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariantScript = productInfoNode?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
      if (!selectedVariantScript) return null;

      try {
        return JSON.parse(selectedVariantScript.innerHTML);
      } catch (e) {
        return null;
      }
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      if (!shouldFetchFullPage) {
        params.push(`section_id=${this.sectionId}`);
      }

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl) {
      return (html) => {
        const variant = this.getSelectedVariant(html);
        this.updateOtfSelection(false);
        const root = this.closest('[id^="MainProduct-"]') || this;
        root.dataset.otfVariantUnavailable = String(!variant?.available);
        if (this.dataset.otfNeedsSize === 'true') {
          this.updateVariantInputs(null);
          this.updateURL(productUrl, null);
          this.updateMedia(html, variant?.featured_media?.id);
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant: null },
          });
          return;
        }
        const sourceButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (sourceButton?.dataset.otfNeedsSize === 'true' && this.dataset.otfNeedsSize === 'false') {
          sourceButton.disabled = sourceButton.dataset.otfUnavailable === 'true';
          sourceButton.querySelector('span').textContent = sourceButton.disabled ? window.variantStrings.soldOut : window.variantStrings.addToCart;
        }
        const pickupAvailability = this.querySelector('pickup-availability');
        if (pickupAvailability && typeof pickupAvailability.update === 'function') {
          pickupAvailability.update(variant);
        }

        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant: null,
            },
          });
          return;
        }
        this.updateMedia(html, variant?.featured_media?.id);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const sourceId = `${id}-${this.sectionId}`;
          const destId = `${id}-${this.dataset.section}`;
          const source = html.getElementById(sourceId);

          let destination = this.querySelector(`#${destId}`);
          if (!destination) {
            destination = document.querySelector(`#${destId}`);
          }

          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
            destination.classList.remove('visibility-hidden');
          }

          const stickySourceId = `${id}-${this.sectionId}-sticky`;
          const stickyDestId = `${id}-${this.dataset.section}-sticky`;
          const stickySource = html.getElementById(stickySourceId);
          const stickyDest = document.querySelector(`#${stickyDestId}`);

          if (stickySource && stickyDest) {
            stickyDest.innerHTML = stickySource.innerHTML;
            stickyDest.classList.toggle('hidden', shouldHide(stickySource));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Main-Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');

        this.updateQuantityRules(this.sectionId, html);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });
      };
    }

    updateVariantInputs(variantId) {
      const selectors = `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`;
      document.querySelectorAll(selectors).forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (input) {
          input.value = variantId || '';
          input.disabled = !variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    updateURL(url, variantId) {
      let destination = `${url}${variantId ? `?variant=${variantId}` : ''}`;
      if (this.otfFeaturedColor && !variantId && this.dataset.otfNeedsSize === 'true') {
        const selected = Array.from(this.querySelectorAll('variant-radios input:checked, variant-selects option:checked')).find(
          (input) => !input.closest('[data-otf-size]') && input.dataset.optionValueId
        );
        if (selected) destination = `${url}?otf_color=${encodeURIComponent(selected.dataset.optionValueId)}`;
      }
      if (variantId) this.otfFeaturedColor = null;
      const shareButton = this.querySelector('share-button');
      if (shareButton && shareButton.updateUrl) {
        shareButton.updateUrl(`${window.shopUrl}${destination}`);
      }

      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', destination);
    }

    setUnavailable() {
      const selectors = ['price', 'Inventory', 'Sku', 'Main-Sku'];
      selectors.forEach((id) => {
        const element = this.querySelector(`#${id}-${this.dataset.section}`);
        if (element) {
          element.classList.add('visibility-hidden');
        }
      });
    }

    updateMedia(html, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;

      const mediaGallery = document.querySelector(`#MediaGallery-${this.dataset.section}`);

      if (mediaGallery && mediaGallery.setActiveMedia) {
        const mediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        mediaGallery.setActiveMedia(mediaId, true);
      }

      const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
      const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
      if (modalContent && newModalContent) {
        modalContent.innerHTML = newModalContent.innerHTML;
      }
    }

    setQuantityBoundries(sourceButton) {
      if (!this.quantityInput) return;

      const data = {
        cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
        min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
        max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
        step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1
      }

      let min = data.min;
      const max = data.max === null ? data.max : data.max - data.cartQuantity;
      if (max !== null) min = Math.min(min, max);
      if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      min = Math.max(0, min);
      this.quantityInput.min = min;
      if (max !== null) {
        this.quantityInput.max = Math.max(0, max);
      } else {
        this.quantityInput.removeAttribute('max');
      }
      this.quantityInput.value = min;
      this.syncQuantityLimitButton(sourceButton);
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
    }


    syncQuantityLimitButton(sourceButton) {
      const addButton = this.productForm?.querySelector('[name="add"]');
      if (!addButton) return;
      const label = addButton.querySelector('span');
      if (sourceButton) {
        this.quantityLimitButtonState = {
          disabled: sourceButton.disabled,
          text: sourceButton.querySelector('span')?.textContent
        };
        if (sourceButton.dataset.quantityLimitText) addButton.dataset.quantityLimitText = sourceButton.dataset.quantityLimitText;
        else delete addButton.dataset.quantityLimitText;
      }

      if (this.quantityInput?.max === '0') {
        if (!this.quantityLimitButtonState) {
          this.quantityLimitButtonState = { disabled: addButton.disabled, text: label?.textContent };
        }
        addButton.disabled = true;
        if (label) label.textContent = this.quantityLimitButtonState.disabled
          ? this.quantityLimitButtonState.text || window.variantStrings.soldOut
          : addButton.dataset.quantityLimitText || window.variantStrings.unavailable;
      } else if (this.quantityLimitButtonState) {
        addButton.disabled = this.quantityLimitButtonState.disabled;
        if (label && this.quantityLimitButtonState.text) label.textContent = this.quantityLimitButtonState.text;
        this.quantityLimitButtonState = null;
      }
    }

    fetchQuantityRules() {
      const currentVariantInput = this.productForm?.querySelector('input[name="id"]');
      if (!currentVariantInput || !currentVariantInput.value) return;

      const loadingSpinner = this.quantityForm?.querySelector('.quantity__rules-cart .loading-overlay');
      if (loadingSpinner) {
        loadingSpinner.classList.remove('hidden');
      }

      const variantId = currentVariantInput.value;
      this.quantityRulesController?.abort();
      const controller = new AbortController();
      this.quantityRulesController = controller;
      fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.sectionId}`, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Quantity rules could not be loaded.');
          return response.text();
        })
        .then((responseText) => {
          if (controller !== this.quantityRulesController || variantId !== currentVariantInput.value || currentVariantInput.disabled || !this.isConnected) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.updateQuantityRules(this.sectionId, html);
        })
        .catch(e => {
          if (e.name === 'AbortError') return;
          console.error(e);
        })
        .finally(() => {
          if (loadingSpinner) {
            loadingSpinner.classList.add('hidden');
          }
        });
    }

    updateQuantityRules(sectionId, html) {
      const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      if (!quantityFormUpdated || !this.quantityForm) return;

      const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      for (let selector of selectors) {
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity__input') {
          const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (let attribute of attributes) {
            const valueUpdated = updated.getAttribute(attribute);
            if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
            else current.removeAttribute(attribute);
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
      this.setQuantityBoundries(html.getElementById(`ProductSubmitButton-${sectionId}`));
    }

    get productForm() {
      return this.querySelector(`product-form`);
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.section;
    }
  }
)};

